import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/lib/content";
import Tilt from "@/components/site/Tilt";

export default function Services() {
  const total = String(SERVICES.length).padStart(2, "0");

  return (
    <section
      id="leistungen"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-white overflow-hidden"
      style={{ perspective: "1200px" }}
    >
      {/* subtle top & bottom separators + soft top glow */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[420px] rounded-full bg-sky-100/40 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        {/* Refined header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-sky-700 mb-5">
              <span className="w-8 h-px bg-sky-600/60" />
              Unsere Leistungen
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[56px] font-semibold text-slate-900 leading-[1.02] tracking-tight">
              Reinigungslösungen,<br className="hidden md:block" />
              <span className="italic font-normal text-slate-500">auf die man sich verlässt.</span>
            </h2>
          </div>
          <p className="text-slate-600 max-w-sm text-base leading-relaxed md:pb-2">
            Vier klar definierte Leistungsbereiche — jeder mit derselben Sorgfalt
            und Verlässlichkeit ausgeführt.
          </p>
        </div>

        {/* Clean symmetric 4-column grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {SERVICES.map((s, i) => {
            const num = String(i + 1).padStart(2, "0");
            return (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="h-full"
              >
                <Tilt max={6} scale={1.012}>
                  <a
                    href="#kontakt"
                    data-testid={`service-card-${s.key}`}
                    className="group relative flex flex-col h-full overflow-hidden rounded-3xl bg-white border border-slate-200 shadow-[0_4px_20px_-10px_rgba(15,23,42,0.1)] hover:border-sky-200 hover:shadow-[0_30px_70px_-25px_rgba(2,132,199,0.4)] transition-all duration-500"
                  >
                    {/* Image */}
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={s.image}
                        alt={s.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.07]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-slate-950/10 to-transparent" />

                      {/* Number */}
                      <div className="absolute top-4 left-5 flex items-baseline gap-1.5">
                        <span className="font-display text-[42px] leading-none font-semibold text-white tracking-tight drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)]">
                          {num}
                        </span>
                        <span className="text-[10px] font-semibold tracking-[0.2em] text-white/75 pb-1">
                          / {total}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div
                      className="flex flex-col justify-between flex-1 p-6 md:p-7"
                      style={{ transform: "translateZ(15px)" }}
                    >
                      <div>
                        <h3 className="font-display text-xl font-semibold text-slate-900 tracking-tight leading-tight">
                          {s.title}
                        </h3>
                        <div className="mt-3 h-px w-10 bg-sky-500/70 group-hover:w-20 transition-all duration-500" />
                        <p className="mt-4 text-sm text-slate-600 leading-relaxed line-clamp-4">
                          {s.desc}
                        </p>
                      </div>
                      <div
                        data-testid={`service-link-${s.key}`}
                        className="mt-6 flex items-center justify-between gap-3 pt-5 border-t border-slate-100"
                      >
                        <span className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
                          Anfrage stellen
                        </span>
                        <span className="grid place-items-center w-9 h-9 rounded-full bg-slate-900 text-white transition-all group-hover:bg-sky-600 group-hover:rotate-45">
                          <ArrowUpRight className="w-4 h-4" strokeWidth={2.4} />
                        </span>
                      </div>
                    </div>
                  </a>
                </Tilt>
              </motion.div>
            );
          })}
        </div>

        {/* Footer CTA strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-14 md:mt-16 flex flex-col md:flex-row items-center justify-between gap-5 rounded-2xl bg-slate-50 border border-slate-200 px-7 py-6"
        >
          <p className="font-display text-lg md:text-xl text-slate-900 text-center md:text-left">
            Passendes Leistungspaket gesucht?
            <span className="text-slate-500 font-normal"> Wir beraten Sie unverbindlich.</span>
          </p>
          <a
            href="#kontakt"
            data-testid="services-cta"
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-sky-600 text-white px-6 py-3 text-sm font-semibold shadow-lg shadow-slate-900/10 transition-all"
          >
            Angebot anfragen <ArrowUpRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
